/**
 * Auth store. Persists the JWT token and current user to localStorage
 * so a page refresh doesn't kick the user back to login.
 *
 * The token is attached to every API request by the client wrapper in
 * src/api/client.ts.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { User } from "@vms/tubeforge-shared";

interface AuthState {
  user: User | null;
  token: string | null;
  expiresAt: number | null; // epoch ms

  setAuth: (params: { user: User; token: string; expiresAt: Date }) => void;
  updateUser: (user: User) => void;
  clearAuth: () => void;

  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      expiresAt: null,

      setAuth: ({ user, token, expiresAt }) =>
        set({
          user,
          token,
          expiresAt: new Date(expiresAt).getTime(),
        }),

      updateUser: (user) => set({ user }),

      clearAuth: () => set({ user: null, token: null, expiresAt: null }),

      isAuthenticated: () => {
        const { token, expiresAt } = get();
        if (!token || !expiresAt) return false;
        return Date.now() < expiresAt;
      },
    }),
    {
      name: "tubeforge-auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        expiresAt: state.expiresAt,
      }),
    },
  ),
);
